import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '../components/states'
import { FreshnessBanner } from '../components/FreshnessBanner'
import { LineCard } from '../components/LineCard'
import type { OrderLine } from '../data/orders'
import { fetchSkuOrderLines } from '../data/sku'
import { fetchItemStock, type ItemStock } from '../data/stock'
import { formatAsOf, formatCurrency, formatIdentifier, formatQuantity } from '../lib/format'
import {
  NOT_A_CATALOG_ITEM,
  PRICE_MISSING,
  SKU_LIST_PRICE_LEAD,
  SKU_LIST_PRICE_QUALIFIER,
  STOCK_UNKNOWN,
} from '../lib/labels'
import './SkuScreen.css'

/** A rep can have hundreds of lines against one SKU; this is a phone screen. */
const PAGE_SIZE = 20

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; detail: string }
  | { status: 'not_found' }
  | {
      status: 'ready'
      /** null = not a PIMBERLY catalog item (no row in rep_stock_price) */
      item: ItemStock | null
      lines: OrderLine[]
      totalCount: number
    }

/**
 * Route `/sku/:itemId` — the Script Manager deep-link target (2.3), and
 * where Products search results lead. Arriving here cold and
 * unauthenticated is the NORMAL case; RequireAuth (the `Gated` wrapper in
 * App.tsx) renders sign-in in its place, so everything below only ever runs
 * authorized.
 *
 * Stock and ESL list price come from public.rep_stock_price (data/stock.ts):
 * the website's own Available number and price1 only. No tier price and no
 * per-location quantity exists on this screen or in its responses.
 *
 * Net price does not exist on this screen. The source ("D3") is unresolved;
 * there is no placeholder row for it, which would imply a number is coming
 * when none is.
 */
export function SkuScreen() {
  const { itemId } = useParams<{ itemId: string }>()
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const [limit, setLimit] = useState(PAGE_SIZE)

  const load = useCallback(
    (linesLimit: number) => {
      if (!itemId) return
      setState({ status: 'loading' })

      void Promise.all([fetchItemStock(itemId), fetchSkuOrderLines(itemId, linesLimit)]).then(
        ([stockResult, linesResult]) => {
          if (stockResult.status === 'error') {
            setState({ status: 'error', detail: stockResult.detail })
            return
          }
          if (linesResult.status === 'error') {
            setState({ status: 'error', detail: linesResult.detail })
            return
          }
          // Only "not a catalog item AND no order line" reads as not found —
          // an item outside PIMBERLY can still have past order lines, shown
          // below with the "not a stocked catalog item" note.
          if (stockResult.status === 'not_found' && linesResult.lines.length === 0) {
            setState({ status: 'not_found' })
            return
          }
          setState({
            status: 'ready',
            item: stockResult.status === 'found' ? stockResult.item : null,
            lines: linesResult.lines,
            totalCount: linesResult.totalCount,
          })
        },
      )
    },
    [itemId],
  )

  // Reset paging when the route lands on a different item, so a "show more"
  // click on one SKU doesn't carry its expanded limit into the next.
  useEffect(() => {
    setLimit(PAGE_SIZE)
  }, [itemId])

  useEffect(() => {
    load(limit)
  }, [load, limit])

  const retry = useCallback(() => load(limit), [load, limit])
  const showMore = useCallback(() => setLimit((n) => n + PAGE_SIZE), [])

  if (!itemId) {
    return <EmptyState title="No item specified" />
  }

  if (state.status === 'loading') {
    return <LoadingState label="Loading item…" />
  }

  if (state.status === 'error') {
    return <ErrorState title="We could not load this item" detail={state.detail} onRetry={retry} />
  }

  if (state.status === 'not_found') {
    return <EmptyState title="Item not found" detail={`No item matches ${formatIdentifier(itemId)}.`} />
  }

  const { item, lines, totalCount } = state
  const remaining = totalCount - lines.length
  // itemDescription is product copy, not a SKU — left as-is. The itemId
  // fallback (only reached with no description anywhere) IS a SKU.
  const title = item?.itemDescription ?? lines[0]?.itemDescription ?? formatIdentifier(itemId)

  return (
    <article className="esl-sku">
      <FreshnessBanner />
      <p className="esl-identifier">{formatIdentifier(itemId)}</p>
      <h1>{title}</h1>

      {item ? (
        <>
          {/* The SKU page is not scoped to a customer, so it never shows a
              rep-specific number (Jeff, 09-03, reaffirmed 09-07). ESL list
              price is the one hero. A missing price1 says so in words —
              never $0.00 — and then there is no price to qualify. */}
          <div className="esl-hero">
            {SKU_LIST_PRICE_LEAD ? <p className="esl-hero-label">{SKU_LIST_PRICE_LEAD}</p> : null}
            {item.listPrice !== null ? (
              <>
                <p className="esl-hero-value">{formatCurrency(item.listPrice)}</p>
                <p className="esl-sku__hero-note--warning">{SKU_LIST_PRICE_QUALIFIER}</p>
              </>
            ) : (
              <p className="esl-sku__price-missing">{PRICE_MISSING}</p>
            )}
          </div>

          {/* One number, the website's own (task decision 2026-11-02): no
              per-location breakdown. Unknown is words, never 0. */}
          <div className="esl-sku__stats">
            <div className="esl-sku__stat">
              <p className="esl-sku__stat-label">Available</p>
              {item.available !== null ? (
                <p className="esl-sku__stat-value">{formatQuantity(item.available)}</p>
              ) : (
                <p className="esl-sku__stat-unknown">{STOCK_UNKNOWN}</p>
              )}
              <p className="esl-sku__stat-note">{formatAsOf(item.syncedAt)}</p>
            </div>
          </div>
        </>
      ) : (
        <p className="esl-muted esl-sku__no-stock">{NOT_A_CATALOG_ITEM}</p>
      )}

      <section className="esl-sku__orders">
        <h2>Your Order Lines for This Item</h2>

        {lines.length === 0 ? (
          <EmptyState title="No order lines yet" detail="You have no orders that include this item." />
        ) : (
          <>
            {/* Newest order first (data/sku.ts). Same line card as order
                detail: cancelled lines greyed, never hidden; dates are ESL
                promise / Updated / Date pending only. No stock on the lines
                (JCP 2026-11-02: keep lines lean). */}
            <ul className="esl-line-list">
              {lines.map((line) => (
                <LineCard
                  key={`${line.orderNo}-${line.lineNo}`}
                  line={line}
                  heading={
                    <Link to={`/orders/${line.orderNo}`} className="esl-line-card__link">
                      {formatIdentifier(line.orderNo)}
                    </Link>
                  }
                />
              ))}
            </ul>

            {remaining > 0 ? (
              <button type="button" className="esl-btn esl-btn--secondary" onClick={showMore}>
                Show {Math.min(remaining, PAGE_SIZE)} more
              </button>
            ) : null}
          </>
        )}
      </section>
    </article>
  )
}
